import { useMutation, useQueryClient } from '@tanstack/react-query';
import { surveysQueryKeys } from '../Constants/queryKeys';
import { purgeSurvey } from '../Services/Surveys.service';
import { useActionToast } from '@/Hooks/useActionToast';

/**
 * Erase every answer of a survey (R26) — `POST /:id/purge`, with the slug typed back.
 *
 * The screen after this call has to agree with itself in one frame: the respondents list, the
 * comments, the funnel, the averages, "Enviaram", the response rate and the card's own counter all
 * counted the rows that just went. Zero respondents beside an average of 4,2 is worse than an
 * error, because nothing on screen looks broken.
 *
 * So everything about this survey is invalidated, by **prefix**: the purge crosses every version,
 * and the results of version 1 are as wrong afterwards as the version on screen.
 *
 * Nothing is optimistic here either. The caller closes the dialog on settle and the numbers change
 * when the refetch brings them back, not before.
 */
export const usePurgeSurvey = (surveyId: string) => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  return useMutation({
    mutationFn: (confirmSlug: string) => purgeSurvey(surveyId, confirmSlug),
    onSuccess: result => {
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.allResponsesOf(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.allResultsOf(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.allCommentsOf(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.detail(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.lists() });

      toast.success(
        result.deletedResponses === 1
          ? '1 resposta apagada'
          : `${result.deletedResponses.toLocaleString('pt-BR')} respostas apagadas`,
        'A pesquisa e o questionário continuam como estavam. Quem tinha respondido volta a ficar elegível.',
      );
    },
    onError: (error: Error) => {
      /*
        Refetched on failure for the same reason as the individual erasure: nothing is removed
        here, the screen is put back in step with the BFF. A `400` deleted nothing, so the numbers
        come back exactly as they were — which is the honest answer to a slug that did not match.
      */
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.allResultsOf(surveyId) });
      toast.failure('Não foi possível apagar as respostas', error);
    },
  });
};
